import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { genPrice } from '@/utils/price';
import { ProviderType, TrackingEvent } from '../types';
import { CheckoutData, CheckoutResultData } from './types';

interface PriceData {
  total: string;
  subtotal: string;
  discount: string;
  tmp?: {
    carts?: Array<{
      buried_point_data?: {
        campaign_type_id_list?: number[];
        campaign_sku_list?: string[];
      };
    }>;
  };
}

/**
 * 提取活动相关数据
 */
const extractCampaignData = (priceData: PriceData) => {
  const campaign_type_id_list: number[] = [];
  const campaign_sku_list: string[] = [];

  const carts = priceData?.tmp?.carts || [];
  carts.forEach((item) => {
    if (item.buried_point_data?.campaign_type_id_list?.length) {
      campaign_type_id_list.push(...item.buried_point_data.campaign_type_id_list);
    }
    if (item.buried_point_data?.campaign_sku_list?.length) {
      campaign_sku_list.push(...item.buried_point_data.campaign_sku_list);
    }
  });

  return {
    campaign_type_id_list: Array.from(new Set(campaign_type_id_list)),
    campaign_sku_list: Array.from(new Set(campaign_sku_list)),
  };
};

/**
 * 结算开始事件适配器
 */
export function checkoutClickAdapter(
  data: CheckoutData,
  provider: ProviderType,
  eventId?: string,
): TrackingEvent | null {
  const { cartItems, priceData, currency, cartViewUuid, entranceCartScene, ttqEventId } = data;

  // 计算基础数据
  const finalPrice = priceData.subtotal
    ? parseFloat(priceData.subtotal.replace(/[^\d.]/g, ''))
    : '0';
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const contentIds = cartItems.map((item) => item.product.shop_sku);

  switch (provider) {
    case ProviderType.GA4:
      return {
        commandType: 'event',
        eventName: 'proceed_checkout',
        properties: {
          event_label: 'proceed_checkout',
          event_category: 'ADD_CART',
          productId: cartItems.map((i) => i.product?.shop_sku || '').join(','),
          productName: cartItems.map((i) => i.product?.short_name || '').join(','),
          productQuantity: cartItems.map((i) => i.qty || 0).join(','),
          productPrice: cartItems
            .map((i) => genPrice(i.product?.price_info, currency?.name))
            .join(','),
          productVariant: cartItems.map((i) => i.product?.color || '').join(','),
        },
      };
    case ProviderType.TIKTOK:
      return {
        eventName: 'InitiateCheckout',
        eventId,
        properties: {
          quantity: totalQuantity,
          content_type: 'product',
          content_ids: contentIds,
          currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          value: finalPrice,
          event_id: eventId || ttqEventId, // 优先使用统一的 eventId
          server_report_data: {
            contents: cartItems.map((item) => ({
              content_id: item.product?.shop_sku || '',
              content_type: 'product',
            })),
            currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
            value: finalPrice,
          },
        },
      };

    case ProviderType.APPLOVIN: {
      if (!cartItems || cartItems.length === 0) {
        logger.warn('没有选中商品，跳过Axon埋点');
        return null;
      }

      // 构建Axon商品数据
      const axonItems = cartItems.map((item) => {
        const sellPrice =
          genPrice(item.product?.price_info, env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency)
            ?.sell_price || '0';
        return {
          item_id: item.product?.spu || '',
          item_variant_id: item.product?.shop_sku || '',
          image_url:
            item.product?.image?.celebrity?.[0] ||
            item.product?.image?.material?.[0] ||
            item.product?.image?.product?.[0] ||
            '',
          item_name: item.product?.short_name || '',
          price: parseFloat(sellPrice),
          quantity: item.qty || 1,
        };
      });
      return {
        eventName: 'begin_checkout',
        properties: {
          currency: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency,
          items: axonItems,
          value: finalPrice,
        },
      };
    }

    case ProviderType.SENSORS: {
      const productQuantity = cartItems.reduce((sum, item) => sum + (item.qty || 0), 0);
      const campaignData = extractCampaignData(priceData);
      return {
        eventName: 'CartCheckOut_Click',
        eventId,
        properties: {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          shop_sku_list: cartItems.map((item) => item.product?.shop_sku || ''),
          business_sku_list: cartItems.map((item) => item.product?.business_sku || ''),
          product_sku_list: cartItems.map((item) => item.product?.sku || ''),
          product_id_list: cartItems.map((item) =>
            String(item.product_id || item.product?.id || ''),
          ),
          product_name_list: cartItems.map((item) => item.product?.short_name || ''),
          product_quantity: productQuantity,
          product_amount: parseFloat(priceData.total || '0'),
          product_actual_amount: parseFloat(priceData.subtotal || '0'),
          product_discount: parseFloat(priceData.discount || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
          currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          campaign_sku_list: campaignData.campaign_sku_list,
          campaign_type_id_list: campaignData.campaign_type_id_list,
        },
      };
    }

    case ProviderType.RTB_HOUSE:
      return {
        eventName: 'startorder',
        eventId,
      };
    case ProviderType.SNOWPLOW: {
      const productQuantity = cartItems.reduce((sum, item) => sum + (item.qty || 0), 0);
      const campaignData = extractCampaignData(priceData);
      return {
        eventName: 'CartCheckOut_Click',
        eventId,
        properties: {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          shop_sku_list: cartItems.map((item) => item.product?.shop_sku || ''),
          product_quantity: productQuantity,
          product_amount: parseFloat(priceData.total || '0'),
          product_actual_amount: parseFloat(priceData.subtotal || '0'),
          product_discount: parseFloat(priceData.discount || '0'),
          currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          campaign_sku_list: campaignData.campaign_sku_list,
          campaign_type_id_list: campaignData.campaign_type_id_list,
        },
      };
    }
    default:
      return null;
  }
}

/**
 * 结算结果事件适配器
 */
export function checkoutResultAdapter(
  data: CheckoutResultData,
  provider: ProviderType,
  eventId?: string,
): TrackingEvent | null {
  const {
    isCheckoutSuccess,
    serviceCode,
    msg,
    cartItems,
    priceData,
    currency,
    cartViewUuid,
    entranceCartScene,
  } = data;

  const productQuantity = cartItems.reduce((sum, item) => sum + (item.qty || 0), 0);
  const campaignData = extractCampaignData(priceData);
  switch (provider) {
    case ProviderType.SENSORS:
      return {
        eventName: 'CartCheckOut_Result',
        eventId,
        properties: {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          is_checkout_success: isCheckoutSuccess,
          service_code: serviceCode,
          shop_sku_list: cartItems.map((item) => item.product?.shop_sku || ''),
          business_sku_list: cartItems.map((item) => item.product?.business_sku || ''),
          product_sku_list: cartItems.map((item) => item.product?.sku || ''),
          product_id_list: cartItems.map((item) => String(item.product_id)),
          product_name_list: cartItems.map((item) => item.product?.short_name || ''),
          product_quantity: productQuantity,
          product_amount: parseFloat(priceData.total || '0'),
          product_actual_amount: parseFloat(priceData.subtotal || '0'),
          product_discount: parseFloat(priceData.discount || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '1'),
          currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          campaign_sku_list: campaignData.campaign_sku_list,
          campaign_type_id_list: campaignData.campaign_type_id_list,
          msg,
        },
      };
    case ProviderType.SNAPCHAT: {
      return {
        eventName: 'START_CHECKOUT',
        eventId,
        properties: {
          price: parseFloat(priceData.subtotal),
          currency: currency?.name,
          item_ids: cartItems.map((item) => item.product?.shop_sku),
          item_category: 'eyewear',
          number_items: productQuantity,
        },
      };
    }
    case ProviderType.SNAPCHAT_CONVERSION: {
      return {
        eventName: 'START_CHECKOUT',
        eventId,
        properties: {
          currency: currency?.name, // 货币代码（如 USD, EUR）
          value: parseFloat(priceData.subtotal), // 订单总价（数字类型）
          contents: cartItems.map((item) => {
            const sellPrice = genPrice(item.product?.price_info, currency?.name)?.sell_price || '0';
            return {
              id: item.product?.shop_sku,
              quantity: item.qty,
              item_price: sellPrice,
            };
          }),
        },
      };
    }
    case ProviderType.SNOWPLOW:
      return {
        eventName: 'CartCheckOut_Result',
        eventId,
        properties: {
          cart_view_uuid: cartViewUuid,
          entrance_cart_scene: entranceCartScene,
          is_checkout_success: isCheckoutSuccess,
          service_code: serviceCode,
          shop_sku_list: cartItems.map((item) => item.product?.shop_sku || ''),
          product_quantity: productQuantity,
          product_amount: parseFloat(priceData.total || '0'),
          product_actual_amount: parseFloat(priceData.subtotal || '0'),
          product_discount: parseFloat(priceData.discount || '0'),
          currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
          msg,
        },
      };
    default:
      return null;
  }
}
